import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/components/feature/AdminAuthContext';
import Logo from '@/components/feature/Logo';

export default function AdminSettings() {
  const { staffName, staffEmail, staffRole, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <header className="bg-primary-900 sticky top-0 z-40">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            <div className="flex items-center gap-3">
              <Logo variant="light" className="w-[130px] md:w-[170px]" />
              <span className="hidden sm:block font-heading font-bold text-sm md:text-base text-background-50 whitespace-nowrap">PVS Admin</span>
            </div>
            <div className="hidden md:flex items-center gap-1">
              <Link to="/admin" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Dashboard</Link>
              <Link to="/admin/appointments" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Appointments</Link>
              <Link to="/admin/applications" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">All Applications</Link>
              <Link to="/admin/reports" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Reports</Link>
              <Link to="/admin/settings" className="px-3 py-2 text-sm font-medium text-background-50 bg-background-50/10 rounded-md whitespace-nowrap cursor-pointer">Settings</Link>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-background-50/80 text-sm">{staffName}</span>
              <button onClick={handleLogout} className="px-3 py-1.5 bg-background-50/10 text-background-50 text-xs font-medium rounded-md border border-background-50/20 hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer">Logout</button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-8 h-8 flex items-center justify-center text-background-50 cursor-pointer"><i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i></button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-3 flex flex-col gap-1">
              <Link to="/admin" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Dashboard</Link>
              <Link to="/admin/appointments" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Appointments</Link>
              <Link to="/admin/applications" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">All Applications</Link>
              <Link to="/admin/reports" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Reports</Link>
              <Link to="/admin/settings" className="px-3 py-2 text-sm font-medium text-background-50 bg-background-50/10 rounded-md whitespace-nowrap cursor-pointer">Settings</Link>
            </div>
          )}
        </div>
      </header>

      <main className="py-8 md:py-10">
        <div className="w-full max-w-[900px] mx-auto px-4 md:px-8">
          <div className="mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Staff Profile & Settings</h2>
            <p className="text-sm text-foreground-600 mt-1">Manage your account and preferences</p>
          </div>

          {saved && (
            <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-md p-3 mb-6 flex items-center gap-2"><i className="ri-check-line"></i> Changes saved successfully.</div>
          )}

          <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6 mb-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-primary-500 text-background-50 rounded-full">
                <span className="font-heading font-bold text-xl">{staffName.charAt(0)}</span>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-foreground-900">{staffName}</h3>
                <p className="text-sm text-foreground-500">{staffEmail}</p>
                <p className="text-xs text-foreground-400 mt-0.5">Role: {staffRole}</p>
              </div>
            </div>
          </div>

          <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6 mb-6">
            <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Edit Profile</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Full Name</label>
                <input type="text" defaultValue={staffName} className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Email</label>
                <input type="email" defaultValue={staffEmail} className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" />
              </div>
            </div>
            <button onClick={handleSave} className="px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors text-sm whitespace-nowrap cursor-pointer">Save Profile</button>
          </div>

          <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6 mb-6">
            <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Change Password</h3>
            <p className="text-sm text-foreground-500 mb-4">Password changes are managed through the Supabase authentication system. Contact your admin to reset your password.</p>
          </div>

          <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6">
            <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {[
                { action: 'Logged in', time: 'Today, 09:15 AM', icon: 'ri-login-box-line', color: 'bg-green-100 text-green-600' },
                { action: 'Updated applications', time: 'Today, 10:30 AM', icon: 'ri-edit-line', color: 'bg-primary-100 text-primary-600' },
                { action: 'Viewed reports', time: 'Today, 11:45 AM', icon: 'ri-bar-chart-line', color: 'bg-accent-100 text-accent-600' },
              ].map((a, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center ${a.color} rounded-full flex-shrink-0`}><i className={`${a.icon} text-sm`}></i></div>
                  <div className="flex-1"><p className="text-sm text-foreground-800">{a.action}</p><p className="text-xs text-foreground-500">{a.time}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}